/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { useRetracked } from 'js/lib/retracked';
import { LONG_MONTH_YEAR_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Grid, IconButton, Link, Skeleton, Tag, Typography2 } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import {
  Badge as ChallengeAchievementBadge,
  RankMessage,
} from 'bundles/account-profile/components/recruiting-profile-viewer/components/projects/ChallengeAchievement';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import type { SharedLearnerProfile_PortfolioProjectsFragment as PortfolioProject } from 'bundles/account-profile/components/recruiting-profile-viewer/queries/__generated__/SharedLearnerProfile_portfolioProjects';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

const IMG_SIZE = 107;

const styles = {
  descriptionText: css`
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;

    // This is a bit hacky, but it's needed because when the CML renders, there's a margin-top added that's interfering with designs
    // Here's a screenshot of what it would look like without this: https://drive.google.com/file/d/1uWXsQKz5IV_MK84sn06RHefnaAv2hbRM/view?usp=sharing
    [data-testid='cml-viewer'] > p {
      margin-top: 0;
    }
  `,
  placeholderImg: () => css`
    width: ${IMG_SIZE}px;
    height: ${IMG_SIZE}px;
    background-color: var(--cds-color-grey-25);
  `,
  previewImg: () => css`
    border-radius: 3px;
    border: 1px solid var(--cds-color-grey-25);
    margin-right: var(--cds-spacing-300);
    height: fit-content;
  `,
  actualImg: css`
    object-fit: contain;
  `,
  withMarginRight: () => css`
    margin-right: var(--cds-spacing-100);
  `,
  withMarginLeft: () => css`
    margin-left: var(--cds-spacing-100);
  `,
  withMarginBottom: () => css`
    margin-bottom: var(--cds-spacing-100);
  `,
};

type PropsFromCaller = {
  project: PortfolioProject;
  onViewCaseStudy: () => void;
  // probably return the project once everything is wired
  onEdit?: () => void;
};

export const PortfolioProjectSkeleton = () => {
  return (
    <Grid container direction="row" role="status">
      <Skeleton width={IMG_SIZE} variant="rect" css={styles.previewImg} />
      <Grid item container direction="column" sm={9} md={10} xl={11} justifyContent="flex-start">
        <Typography2 variant="subtitleMedium" component="h4">
          <Skeleton />
        </Typography2>
        <Typography2 component="p" variant="bodyPrimary" css={[styles.descriptionText, styles.withMarginBottom]}>
          <Skeleton />
          <Skeleton />
        </Typography2>
        <Typography2 component="p" variant="bodySecondary" color="supportText">
          <Skeleton width="25%" />
        </Typography2>
      </Grid>
    </Grid>
  );
};

const PortfolioProjectItem = ({ project, onViewCaseStudy, onEdit }: PropsFromCaller) => {
  const retrackedTrack = useRetracked();
  const { editable, sharedEventingV3Data } = useContext(ProfileContext);
  const { id, title, summary, skills, estimatedWorkload, completed, image, challengeAchievement } = project;
  // eslint-disable-next-line camelcase
  const projectTrackingData = { project_id: id };

  const track = useTracker();
  const editProjectVisibilityTrackingRef: React.MutableRefObject<HTMLButtonElement | null> = useVisibilityTracker(
    'view_button',
    {
      button: {
        name: EVENTS.profileEditProject,
      },
      product: {
        id,
      },
      ...sharedEventingV3Data,
    }
  );

  const viewProjectVisibilityTrackingRef: React.MutableRefObject<HTMLButtonElement | null> = useVisibilityTracker(
    'view_button',
    {
      button: {
        name: EVENTS.profileViewProjectDetails,
      },
      product: {
        id,
      },
      ...sharedEventingV3Data,
    }
  );

  const handleEditClick = () => {
    retrackedTrack({
      trackingName: EVENTS.profileEditProject,
      trackingData: projectTrackingData,
      action: 'click',
    });

    track('click_button', {
      button: {
        name: EVENTS.profileEditProject,
      },
      product: {
        id,
      },
      ...sharedEventingV3Data,
    });

    onEdit?.();
  };

  const handleViewProjectClick = () => {
    retrackedTrack({
      trackingName: EVENTS.profileViewProjectDetails,
      trackingData: projectTrackingData,
      action: 'click',
    });

    track('click_button', {
      button: {
        name: EVENTS.profileViewProjectDetails,
      },
      product: {
        id,
      },
      ...sharedEventingV3Data,
    });

    onViewCaseStudy();
  };

  return (
    <Grid container direction="row" wrap="nowrap" justifyContent="space-between">
      <Grid container direction="row">
        {image ? (
          <div css={styles.previewImg}>
            <Imgix css={styles.actualImg} width={IMG_SIZE} src={image} alt={_t('#{title} preview image', { title })} />
          </div>
        ) : (
          <div css={[styles.placeholderImg, styles.previewImg]} />
        )}
        <Grid item container direction="column" xs={9} justifyContent="flex-start" wrap="nowrap">
          <ChallengeAchievementBadge achievement={challengeAchievement} />
          <Typography2 variant="subtitleMedium" component="h4">
            {title}
          </Typography2>
          {summary && (
            <Typography2 component="p" variant="bodyPrimary" css={[styles.descriptionText, styles.withMarginBottom]}>
              {summary}
            </Typography2>
          )}
          <Grid item container alignItems="center">
            <div>
              {skills.map((tag) => (
                <Tag
                  key={tag}
                  variant="category"
                  priority="primary"
                  css={[styles.withMarginRight, styles.withMarginBottom]}
                  data-testid="skill-tag"
                  onClick={() => {
                    // Wrapping the tag in a <TrackedDiv> butchers the styling, so I'm using the hook since it's just as effective
                    // and simpler to implement
                    retrackedTrack({
                      // not tracked in eventing v3
                      trackingName: 'profile_project_skill_tag',
                      trackingData: {
                        ...projectTrackingData,
                        skill: tag,
                      },
                      action: 'click',
                    });
                  }}
                >
                  {tag}
                </Tag>
              ))}
            </div>
          </Grid>
          <TrackedDiv
            trackClicks={false}
            trackingName={EVENTS.profileViewProjectDetails}
            data={projectTrackingData}
            requireFullyVisible={false}
            withVisibilityTracking
            css={styles.withMarginBottom}
          >
            {/* CDS advises to use the Link component for Buttons styled like a Link:
            https://coursera.slack.com/archives/CPJET5H4L/p1666101555474689?thread_ts=1666100134.598789&cid=CPJET5H4L
            but this causes a lint error because no href is defined */}
            {/*  eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link
              ref={viewProjectVisibilityTrackingRef}
              variant="standard"
              component="button"
              typographyVariant="bodyPrimary"
              onClick={handleViewProjectClick}
            >
              {_t('View project')}
            </Link>
          </TrackedDiv>
          <Typography2 component="p" variant="bodySecondary" color="supportText">
            <RankMessage achievement={challengeAchievement} />
            {estimatedWorkload && (
              <>
                <span css={[styles.withMarginLeft, styles.withMarginRight]}>•</span>
                {estimatedWorkload}
              </>
            )}
            {completed && (
              <>
                {/* FormattedMessage gets rid of extra spacing, which is why I pulled the bullet point out */}
                <span css={[styles.withMarginLeft, styles.withMarginRight]}>•</span>
                <FormattedMessage
                  message={_t('Completed {completionDate}')}
                  completionDate={formatDateTimeDisplay(completed, LONG_MONTH_YEAR_DISPLAY)}
                />
              </>
            )}
          </Typography2>
        </Grid>
      </Grid>
      {editable && (
        <TrackedDiv
          trackClicks={false}
          trackingName={EVENTS.profileEditProject}
          withVisibilityTracking
          requireFullyVisible={false}
          data={projectTrackingData}
        >
          <IconButton
            ref={editProjectVisibilityTrackingRef}
            onClick={handleEditClick}
            intent="edit"
            aria-label={_t('Edit details for project: #{title}', { title })}
            variant="ghost"
            size="small"
            data-testid="edit-project-button"
          />
        </TrackedDiv>
      )}
    </Grid>
  );
};

export default PortfolioProjectItem;
